import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider, useRouteError } from "react-router-dom";
import App from "./App.tsx";
import "./index.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/charts/styles.css";
import "mantine-contextmenu/styles.css";
import "mantine-datatable/styles.css";

import { createTheme, MantineProvider } from "@mantine/core";
import { LoginForm } from "./LoginForm.tsx";
import { QueryClient, QueryClientProvider } from "react-query";
import { RegisterForm } from "./RegisterForm.tsx";
import Positions from "./positions/Positions.tsx";
import { Notifications } from "@mantine/notifications";
import Premium from "./Premium.tsx";
import Stats from "./Stats.tsx";
import { ContextMenuProvider } from "mantine-contextmenu";
import PositionStats from "./PositionStats.tsx";
import RatingCalculator from "./RatingCalculator.tsx";
import * as Sentry from "@sentry/react";
import Exclusions from "./exclusions/Exclusions.tsx";
import Feedback from "./Feedback.tsx";
import Explorer from "./explorer/Explorer.tsx";
import { ForgotPasswordForm } from "./ForgotPasswordForm.tsx";
import { ResetPasswordForm } from "./ResetPasswordForm.tsx";

const queryClient = new QueryClient();

function ErrorBoundary() {
    let error = useRouteError();
    console.error(error);

    Sentry.captureException(new Error(`Error boundary triggered ${error}`));

    return (
        <div>
            This is an error
            <pre>Error details: {JSON.stringify(error)}</pre>
        </div>
    );
}

const router = createBrowserRouter([
    {
        path: "/",
        element: <App />,
        // errorElement: <ErrorBoundary />,
    },
    {
        path: "/login",
        element: <LoginForm />,
    },
    {
        path: "/register",
        element: <RegisterForm />,
    },
    {
        path: "/positions",
        element: <Positions />,
    },
    {
        path: "/premium",
        element: <Premium />,
    },
    {
        path: "/stats/",
        element: <Stats />,
    },
    {
        path: "/stats/:positionId",
        element: <PositionStats />,
    },
    {
        path: "/rating-calculator",
        element: <RatingCalculator />,
    },
    {
        path: "/exclusions",
        element: <Exclusions />,
    },
    {
        path: "/feedback",
        element: <Feedback />,
    },
    {
        path: "/explorer",
        element: <Explorer />,
    },
    {
        path: "/forgot-password",
        element: <ForgotPasswordForm />,
    },
    {
        path: "/reset-password",
        element: <ResetPasswordForm />,
    },
]);

const theme = createTheme({
    colors: {
        dark: [
            "#C1C2C5",
            "#A6A7AB",
            "#909296",
            "#5c5f66",
            "#373A40",
            "#2C2E33",
            "#25262b",
            "#1A1B1E",
            "#141517",
            "#101113",
        ],
    },
});

Sentry.init({
    // dsn: "https://0cfa8d81d0b4bd68592b55d2df62c0cb@o4508728193253376.ingest.de.sentry.io/4508728196792400",
    dsn: "https://f4221859a6c5989347d28e5f06757658@o4509045468954624.ingest.de.sentry.io/4509045471969360",
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            maskAllInputs: false,
        }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    enabled: process.env.NODE_ENV !== "development",
});

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <MantineProvider forceColorScheme="dark" theme={theme}>
            <ContextMenuProvider>
                <Notifications />
                <QueryClientProvider client={queryClient}>
                    <RouterProvider router={router} />
                </QueryClientProvider>
            </ContextMenuProvider>
        </MantineProvider>
    </React.StrictMode>
);
